/*
  Gaia Iluka, Home, The Bottle (3D): loader and scroll driver
  File: assets/gi-home-original.js
  Pairs with sections/gi-home-original.liquid.

  Two jobs. (1) WHEN the 3D viewer (and with it the 1.6 MB model) is
  fetched. (2) The scroll-driven sequence: as the visitor scrolls the
  tall track, the bottle turns, the big word behind it changes and the
  lines in front of it come and go. Nothing moves on its own: every
  value is a function of scroll position, so when the visitor stops,
  everything stops.

  THE GATE IS THE SCRIPT TAG. The section renders a <model-viewer>
  element with its attributes in place but without the component's
  module. Until the module is injected the element is an unknown tag,
  transparent, and the pre-rendered stills behind it carry the picture
  (and the scroll beats, as a four-frame flip). Injecting the module
  upgrades the element, which loads the model and reveals it; the
  section then hides the stills and the scroll drives the real camera.

  LOAD MODES (data-load-mode on the section):
    near       desktop: inject when the section is one viewport away;
               phones: preload then, inject when the section is on screen
    tap        inject when the visitor presses the stage's button
    tap-phone  near at >= 900px, tap below
  Save-Data forces tap; no WebGL means the stills are the whole picture
  and the tap button is never shown.

  SCRUB. Progress p in [0, 1] = how far the sticky stage has travelled
  through the track. Keyframes (azimuth in degrees): p 0 = the editor's
  starting angle (35, three-quarter), 0.3 = 360 (front label), 0.62 =
  540 (back label), 1 = 720 + start (the front label faces out again,
  two turns on, so the logo is what the visitor ends on). Words,
  lines, datelines, dots and the final block each have an in/out
  window on p. Camera controls are switched on only in the last beat,
  so a drag never fights the scroll.

  REDUCED MOTION, NO JS: the section's default CSS is the static final
  beat (the approved chapter). This script adds gi-orig--scrub only
  when motion is allowed; under prefers-reduced-motion it leaves the
  static layout alone and still loads the model near the viewport.

  EVENTS (Shopify.analytics.publish, first party, no destination until
  a custom pixel forwards them): gi_bottle_view, gi_bottle_load,
  gi_bottle_scrolled (reached the last beat), gi_bottle_interact (first
  turn by hand), gi_bottle_cta.
*/
(function () {
  'use strict';

  if (window.__giOriginalLoaded) return;
  window.__giOriginalLoaded = true;

  var DESKTOP = '(min-width: 900px)';
  var REDUCE = '(prefers-reduced-motion: reduce)';
  var moduleRequested = false;

  function publish(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data || {});
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(c.getContext('webgl2') || c.getContext('webgl') || c.getContext('experimental-webgl'));
    } catch (e) { return false; }
  }

  function saveData() {
    var conn = navigator.connection;
    return !!(conn && conn.saveData);
  }

  var modulePreloaded = false;
  function preloadModule(src) {
    if (modulePreloaded || moduleRequested || !src) return;
    modulePreloaded = true;
    var link = document.createElement('link');
    link.rel = 'modulepreload';
    link.href = src;
    document.head.appendChild(link);
  }

  /* Phones (Oct 2026): the modulepreload used to fire during the first
     screen's own loading, where a 283 KB high-priority fetch competes
     with the hero poster, the page's Largest Contentful Paint. Waiting
     for the window load event costs nothing visible: the stills carry
     the chapter until the model arrives, exactly as before. */
  function afterLoad(fn) {
    if (document.readyState === 'complete') { fn(); return; }
    window.addEventListener('load', function () { fn(); }, { once: true });
  }

  function injectModule(src) {
    if (moduleRequested || !src) return;
    moduleRequested = true;
    var tag = document.createElement('script');
    tag.type = 'module';
    tag.src = src;
    document.head.appendChild(tag);
  }

  var clamp01 = function (x) { return x < 0 ? 0 : x > 1 ? 1 : x; };
  var ease = function (t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; };
  /* 1 between b and c, ramps a..b and c..d, 0 outside. */
  function win(p, a, b, c, d) {
    var i = b > a ? clamp01((p - a) / (b - a)) : 1;
    var o = d > c ? 1 - clamp01((p - c) / (d - c)) : 1;
    return Math.min(i, o);
  }

  function setup(section) {
    var stage = section.querySelector('[data-gi-original-stage]');
    var model = section.querySelector('[data-gi-original-model]');
    var tap = section.querySelector('[data-gi-original-tap]');
    var cta = section.querySelector('[data-gi-original-cta]');
    if (!stage || !model) return;

    var viewerSrc = section.getAttribute('data-viewer-src');
    var mode = section.getAttribute('data-load-mode') || 'near';
    var allowTurn = section.getAttribute('data-turn') === 'true';
    var webgl = hasWebGL();
    var viewed = false;
    var loaded = false;
    var turned = false;
    var t0 = 0;

    /* ── View: once, when half the stage is on screen ── */
    if ('IntersectionObserver' in window) {
      var viewIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || viewed) return;
          viewed = true;
          viewIO.disconnect();
          publish('gi_bottle_view', {
            device: window.matchMedia(DESKTOP).matches ? 'desktop' : 'phone',
            mode: mode,
            webgl: webgl,
            reduced_motion: window.matchMedia(REDUCE).matches
          });
        });
      }, { threshold: 0.5 });
      viewIO.observe(stage);
    }

    if (cta) {
      cta.addEventListener('click', function () {
        publish('gi_bottle_cta', { interacted: turned, loaded: loaded });
      });
    }

    /* ── Scroll sequence (independent of whether the model ever loads) ── */
    var scrub = setupScrub(section, model, allowTurn);

    if (!webgl) {
      section.classList.add('gi-orig--fallback');
      return;
    }

    model.addEventListener('load', function () {
      loaded = true;
      section.classList.add('gi-orig--live');
      publish('gi_bottle_load', { ms: t0 ? Math.round(performance.now() - t0) : null });
      if (scrub) {
        scrub.sync();
      } else {
        /* Static layout: nothing else ever touches the camera, and a
           viewer that loaded while off screen can sit on an unpainted
           first frame. One hair of a nudge asks it for a fresh frame. */
        var startDeg = parseFloat(section.getAttribute('data-angle')) || 35;
        window.requestAnimationFrame(function () { model.cameraOrbit = (startDeg + 0.01).toFixed(2) + 'deg 82deg auto'; });
      }
      if (section.classList.contains('gi-orig--hint')) {
        window.setTimeout(function () { section.classList.remove('gi-orig--hint'); }, 4000);
      }
    });
    model.addEventListener('error', function () {
      section.classList.add('gi-orig--fallback');
      section.classList.remove('gi-orig--live', 'gi-orig--hint');
      if (tap) tap.hidden = true;
    });
    model.addEventListener('camera-change', function (event) {
      if (turned) return;
      if (!event.detail || event.detail.source !== 'user-interaction') return;
      turned = true;
      section.classList.add('gi-orig--turned');
      section.classList.remove('gi-orig--hint');
      if (scrub) scrub.handOver();
      publish('gi_bottle_interact', { ms_since_load: t0 ? Math.round(performance.now() - t0) : null });
    });

    function load() {
      if (!t0) t0 = performance.now();
      injectModule(viewerSrc);
    }

    var needsTap =
      mode === 'tap' ||
      saveData() ||
      (mode === 'tap-phone' && !window.matchMedia(DESKTOP).matches);

    if (needsTap && tap) {
      tap.hidden = false;
      tap.addEventListener('click', function () {
        tap.hidden = true;
        section.classList.add('gi-orig--hint');
        load();
      }, { once: true });
      return;
    }

    if (!('IntersectionObserver' in window)) { load(); return; }

    /* "near": desktop injects the module one viewport early. Phones only
       PRELOAD it one viewport early (fetched and compiled off the main
       thread) and inject when the section is actually on screen, so the
       parse and the first frame land while the visitor is looking at the
       stills, not while the hero and the Samos card are still settling.
       Until the model arrives the stills carry the beats. */
    var phone = !window.matchMedia(DESKTOP).matches;
    var nearIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        nearIO.disconnect();
        if (phone) afterLoad(function () { preloadModule(viewerSrc); }); else load();
      });
    }, { rootMargin: '100% 0px 100% 0px' });
    nearIO.observe(section);

    if (phone) {
      var seenIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          seenIO.disconnect();
          load();
        });
      }, { rootMargin: '0px', threshold: 0.1 });
      seenIO.observe(section);
    }
  }

  /* ── The scroll driver ── */
  function setupScrub(section, model, allowTurn) {
    var track = section.querySelector('[data-gi-original-track]');
    if (!track) return null;

    var reduce = window.matchMedia(REDUCE);
    if (reduce.matches) {
      section.classList.add('gi-orig--static');
      return null;
    }
    section.classList.add('gi-orig--scrub');

    /* One direction the whole way, fast at first then steady: start
       (35), a full turn to the front label (360) by 0.3, on to the back
       label (540) by 0.62, and two full turns later the front label
       faces the visitor again at the end (720 + start). No reversal, no
       stop at a keyframe. The stills read angles mod 360. */
    var start = parseFloat(section.getAttribute('data-angle')) || 35;
    var KEYS = [[0, start], [0.3, 360], [0.62, 540], [1, 720 + start]];
    var words = [].slice.call(section.querySelectorAll('[data-gi-word]'));
    var beatlines = [].slice.call(section.querySelectorAll('[data-gi-beatline]'));
    var lines = [].slice.call(section.querySelectorAll('[data-gi-line]'));
    var dots = [].slice.call(section.querySelectorAll('[data-gi-dot]'));
    var stills = [].slice.call(section.querySelectorAll('[data-gi-still]'));
    var finalBlock = section.querySelector('[data-gi-final]');
    /* The olive tree behind everything: drawn from the seed (p 0) to the
       last leaf just as the final beat settles (p 0.92). */
    var treeHost = section.querySelector('.gi-orig__pin');
    var hasTree = !!section.querySelector('[data-gi-tree]');
    var figure = section.querySelector('[data-gi-original-figure]');

    /* in-start, in-end, out-start, out-end on p; index = beat */
    /* The last word hands over to the final block: out as the block comes in. */
    var WORD_W = [[-1, -1, 0.24, 0.34], [0.30, 0.40, 0.66, 0.76], [0.72, 0.80, 0.86, 0.94]];
    /* Line 2 while the front label faces out (around p 0.3), line 3 while
       the back label does (around p 0.62). */
    var LINE_W = [[-1, -1, 0.22, 0.32], [0.27, 0.34, 0.42, 0.48], [0.52, 0.58, 0.74, 0.82]];
    var FINAL_W = [0.88, 0.97, 2, 3];
    var SHIFT_W = [0.84, 0.98, 2, 3];

    /* target = where the scroll is; shown = what is on screen. The two
       meet through a short time-based ease (about 210 ms), which turns
       wheel steps and trackpad bursts into one continuous motion. */
    var target = 0, shown = 0, lastT = 0, active = false, looping = false;
    var userHasCamera = false, scrolledOnce = false;

    function theta(q) {
      for (var i = 1; i < KEYS.length; i++) {
        if (q <= KEYS[i][0]) {
          var a = KEYS[i - 1], b = KEYS[i];
          var t = clamp01((q - a[0]) / (b[0] - a[0]));
          return a[1] + (b[1] - a[1]) * t;
        }
      }
      return KEYS[KEYS.length - 1][1];
    }

    function nearestStill(deg) {
      var best = null, bestD = 1e9;
      stills.forEach(function (img) {
        var d = Math.abs(((parseFloat(img.getAttribute('data-gi-still')) - deg) % 360 + 540) % 360 - 180);
        if (d < bestD) { bestD = d; best = img; }
      });
      return best;
    }

    function setOpacity(el, o) {
      el.style.setProperty('--o', o.toFixed(3));
      el.classList.toggle('is-on', o > 0.5);
    }

    function progress() {
      var r = track.getBoundingClientRect();
      var range = r.height - window.innerHeight;
      if (range <= 0) return 1;
      return clamp01(-r.top / range);
    }

    function render(p) {
      if (hasTree && treeHost) treeHost.style.setProperty('--gi-tree', (1 - ease(clamp01(p / 0.92))).toFixed(4));
      words.forEach(function (el, i) { if (WORD_W[i]) setOpacity(el, win.apply(null, [p].concat(WORD_W[i]))); });
      beatlines.forEach(function (el, i) { if (WORD_W[i]) setOpacity(el, win.apply(null, [p].concat(WORD_W[i]))); });
      lines.forEach(function (el, i) { if (LINE_W[i]) setOpacity(el, win.apply(null, [p].concat(LINE_W[i]))); });
      if (finalBlock) setOpacity(finalBlock, win.apply(null, [p].concat(FINAL_W)));
      if (figure) figure.style.setProperty('--gi-final', ease(win.apply(null, [p].concat(SHIFT_W))).toFixed(3));

      var beat = p < 0.3 ? 0 : p < 0.66 ? 1 : 2;
      dots.forEach(function (d, i) { d.classList.toggle('is-on', i === beat); });

      var deg = theta(p);
      var last = p >= 0.97;
      section.classList.toggle('gi-orig--final', last);

      if (allowTurn) {
        if (last && !model.hasAttribute('camera-controls')) model.setAttribute('camera-controls', '');
        if (!last && model.hasAttribute('camera-controls')) { model.removeAttribute('camera-controls'); userHasCamera = false; }
      }

      if (section.classList.contains('gi-orig--live')) {
        if (!(last && userHasCamera)) model.cameraOrbit = deg.toFixed(2) + 'deg 82deg auto';
      } else if (stills.length) {
        var on = nearestStill(deg);
        stills.forEach(function (img) { img.classList.toggle('is-on', img === on); });
      }

      if (last && !scrolledOnce) { scrolledOnce = true; publish('gi_bottle_scrolled', {}); }
    }

    function frame(now) {
      var dt = lastT ? Math.min(64, now - lastT) : 16;
      lastT = now;
      var k = 1 - Math.exp(-dt / 210);
      var diff = target - shown;
      if (Math.abs(diff) < 0.0004) {
        shown = target;
        render(shown);
        looping = false;
        lastT = 0;
        return;
      }
      shown += diff * k;
      render(shown);
      window.requestAnimationFrame(frame);
    }

    function request() {
      target = progress();
      if (looping) return;
      looping = true;
      window.requestAnimationFrame(frame);
    }

    /* Listen only while the section is on or near the screen. */
    function wake() {
      if (active) return;
      active = true;
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request);
      request();
    }
    function sleep() {
      if (!active) return;
      active = false;
      window.removeEventListener('scroll', request);
      window.removeEventListener('resize', request);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) wake(); else sleep(); });
      }, { rootMargin: '20% 0px 20% 0px' }).observe(section);
    } else {
      wake();
    }
    request();

    reduce.addEventListener('change', function () {
      if (reduce.matches) { sleep(); section.classList.remove('gi-orig--scrub'); section.classList.add('gi-orig--static'); }
      else { section.classList.remove('gi-orig--static'); section.classList.add('gi-orig--scrub'); wake(); }
    });

    return {
      sync: function () { shown = progress(); request(); },
      handOver: function () { userHasCamera = true; }
    };
  }

  function init() {
    document.querySelectorAll('[data-gi-original]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
